import { Message } from '@/types';

export interface RelevantMessage {
  message: Message;
  relevanceScore: number;
  reason: string;
}

export interface SemanticContext {
  relevantMessages: RelevantMessage[];
  contextSummary: string;
  totalMessages: number;
  selectedMessages: number;
}

// Palavras-chave por categoria para análise semântica
const SEMANTIC_CATEGORIES = {
  contracts: [
    'contrato', 'acordo', 'cláusula', 'termo', 'condição', 'locação', 'prestação',
    'serviços', 'compra', 'venda', 'cessão', 'direitos', 'obrigações'
  ],
  litigation: [
    'processo', 'ação', 'petição', 'recurso', 'sentença', 'decisão', 'julgamento',
    'tribunal', 'vara', 'juiz', 'advogado', 'parte', 'autor', 'réu'
  ],
  jurisprudence: [
    'jurisprudência', 'precedente', 'súmula', 'entendimento', 'orientação',
    'stf', 'stj', 'tribunal', 'julgado', 'acórdão', 'decisão'
  ],
  legal_analysis: [
    'análise', 'parecer', 'interpretação', 'aplicação', 'legislação', 'lei',
    'código', 'artigo', 'norma', 'regulamento', 'disposição'
  ],
  calculations: [
    'cálculo', 'valor', 'montante', 'correção', 'juros', 'multa', 'honorários',
    'custas', 'índice', 'atualização', 'base', 'percentual'
  ]
};

// Extrai palavras-chave de uma mensagem
function extractKeywords(content: string): string[] {
  return content
    .toLowerCase()
    .replace(/[^\w\sáàâãéêíóôõúçñ]/g, ' ')
    .split(/\s+/)
    .filter(word => word.length > 3)
    .slice(0, 20); // Limitar a 20 palavras-chave
}

// Calcula similaridade semântica entre duas mensagens
function calculateSemanticSimilarity(message1: string, message2: string): number {
  const keywords1 = extractKeywords(message1);
  const keywords2 = extractKeywords(message2);
  
  if (keywords1.length === 0 || keywords2.length === 0) {
    return 0;
  }
  
  // Calcular interseção de palavras-chave
  const intersection = keywords1.filter(word => keywords2.includes(word));
  const union = [...new Set([...keywords1, ...keywords2])];
  
  // Jaccard similarity
  const jaccardSimilarity = intersection.length / union.length;
  
  // Bonus para correspondências em categorias semânticas
  let categoryBonus = 0;
  for (const [category, terms] of Object.entries(SEMANTIC_CATEGORIES)) {
    const matches1 = terms.filter(term => message1.toLowerCase().includes(term)).length;
    const matches2 = terms.filter(term => message2.toLowerCase().includes(term)).length;
    
    if (matches1 > 0 && matches2 > 0) {
      categoryBonus += 0.2 * Math.min(matches1, matches2);
    }
  }
  
  return Math.min(1.0, jaccardSimilarity + categoryBonus);
}

// Detecta categoria semântica principal de uma mensagem
function detectSemanticCategory(content: string): string {
  const normalizedContent = content.toLowerCase();
  let maxMatches = 0;
  let detectedCategory = 'general';
  
  for (const [category, terms] of Object.entries(SEMANTIC_CATEGORIES)) {
    const matches = terms.filter(term => normalizedContent.includes(term)).length;
    if (matches > maxMatches) {
      maxMatches = matches;
      detectedCategory = category;
    }
  }
  
  return detectedCategory;
}

// Calcula relevância temporal (mensagens mais recentes têm peso maior)
function calculateTemporalRelevance(messageDate: string, currentDate: Date = new Date()): number {
  const msgDate = new Date(messageDate);
  const daysDiff = (currentDate.getTime() - msgDate.getTime()) / (1000 * 60 * 60 * 24);
  
  // Decay exponencial: mensagens perdem relevância ao longo do tempo
  return Math.exp(-daysDiff / 7); // Half-life de 7 dias
}

// Seleciona mensagens relevantes usando análise semântica
export function selectRelevantMessages(
  currentMessage: string,
  conversationHistory: Message[],
  maxMessages: number = 8
): SemanticContext {
  if (conversationHistory.length === 0) {
    return {
      relevantMessages: [],
      contextSummary: 'Nenhuma mensagem anterior disponível.',
      totalMessages: 0,
      selectedMessages: 0
    };
  }
  
  const currentCategory = detectSemanticCategory(currentMessage);
  const relevantMessages: RelevantMessage[] = [];
  
  // Analisar cada mensagem do histórico
  for (const message of conversationHistory) {
    if (!message.content || message.role === 'system' as any) continue;
    
    // Calcular pontuação de relevância
    let relevanceScore = 0;
    const reasons: string[] = [];
    
    // 1. Similaridade semântica
    const semanticSimilarity = calculateSemanticSimilarity(currentMessage, message.content);
    relevanceScore += semanticSimilarity * 0.4;
    if (semanticSimilarity > 0.3) {
      reasons.push(`similaridade semântica: ${(semanticSimilarity * 100).toFixed(1)}%`);
    }
    
    // 2. Categoria semântica
    const messageCategory = detectSemanticCategory(message.content);
    if (messageCategory === currentCategory && messageCategory !== 'general') {
      relevanceScore += 0.3;
      reasons.push(`mesma categoria: ${messageCategory}`);
    }
    
    // 3. Relevância temporal
    const temporalRelevance = calculateTemporalRelevance(message.created_at);
    relevanceScore += temporalRelevance * 0.2;
    if (temporalRelevance > 0.5) {
      reasons.push(`mensagem recente`);
    }
    
    // 4. Tipo de mensagem (perguntas do usuário têm peso maior)
    if (message.role === 'user') {
      relevanceScore += 0.1;
      reasons.push('pergunta do usuário');
    }
    
    // 5. Presença de documentos ou arquivos
    if (message.metadata?.files || message.content.includes('[DOCUMENTO:') || message.content.includes('[IMAGEM')) {
      relevanceScore += 0.2;
      reasons.push('contém documento');
    }
    
    // Adicionar apenas se relevante (score > 0.2)
    if (relevanceScore > 0.2) {
      relevantMessages.push({
        message,
        relevanceScore,
        reason: reasons.join(', ')
      });
    }
  }
  
  // Ordenar por relevância e limitar
  relevantMessages.sort((a, b) => b.relevanceScore - a.relevanceScore);
  const selectedMessages = relevantMessages.slice(0, maxMessages);
  
  // Gerar resumo do contexto
  const contextSummary = generateContextSummary(selectedMessages, currentCategory);
  
  return {
    relevantMessages: selectedMessages,
    contextSummary,
    totalMessages: conversationHistory.length,
    selectedMessages: selectedMessages.length
  };
}

// Gera um resumo do contexto selecionado
function generateContextSummary(relevantMessages: RelevantMessage[], category: string): string {
  if (relevantMessages.length === 0) {
    return 'Nenhum contexto relevante identificado.';
  }
  
  const userMessages = relevantMessages.filter(rm => rm.message.role === 'user');
  const assistantMessages = relevantMessages.filter(rm => rm.message.role === 'assistant');
  
  let summary = `Contexto relevante (${relevantMessages.length} mensagens):`;
  
  // Adicionar informações sobre a categoria principal
  const categoryNames = {
    contracts: 'Contratos e Acordos',
    litigation: 'Processo e Litígio',
    jurisprudence: 'Jurisprudência',
    legal_analysis: 'Análise Jurídica',
    calculations: 'Cálculos Jurídicos',
    general: 'Geral'
  };
  
  summary += `\nCategoria principal: ${categoryNames[category] || 'Geral'}`;
  summary += `\nPerguntas do usuário: ${userMessages.length}`;
  summary += `\nRespostas da assistente: ${assistantMessages.length}`;
  
  // Destacar documentos mencionados
  const documentsCount = relevantMessages.filter(rm => 
    rm.message.content.includes('[DOCUMENTO:') || 
    rm.message.content.includes('[IMAGEM') ||
    rm.message.metadata?.files
  ).length;
  
  if (documentsCount > 0) {
    summary += `\nDocumentos no contexto: ${documentsCount}`;
  }
  
  return summary;
}

// Formatar mensagens relevantes para uso em prompts
export function formatRelevantMessagesForPrompt(semanticContext: SemanticContext): string {
  if (semanticContext.selectedMessages === 0) {
    return '';
  }
  
  let formatted = `\n\n### 📋 CONTEXTO RELEVANTE DA CONVERSA:
${semanticContext.contextSummary}

### 💬 MENSAGENS RELEVANTES:
`;
  
  for (const { message, relevanceScore, reason } of semanticContext.relevantMessages) {
    const role = message.role === 'user' ? '👤 USUÁRIO' : '🤖 ASSISTENTE';
    const content = message.content.length > 200 
      ? message.content.substring(0, 200) + '...'
      : message.content;
    
    formatted += `
**${role}** (relevância: ${(relevanceScore * 100).toFixed(1)}% - ${reason}):
${content}
---`;
  }
  
  return formatted;
}

// Cache para melhorar performance
const semanticCache = new Map<string, SemanticContext>();
const CACHE_SIZE_LIMIT = 100;

export function getCachedSemanticContext(
  currentMessage: string,
  conversationHistory: Message[],
  maxMessages: number = 8
): SemanticContext {
  // Criar chave do cache baseada na mensagem atual e histórico recente
  const historyHash = conversationHistory
    .slice(-20)
    .map(m => m.id)
    .join('|');
  const cacheKey = `${currentMessage.substring(0, 100)}|${historyHash}|${maxMessages}`;
  
  // Verificar cache
  if (semanticCache.has(cacheKey)) {
    return semanticCache.get(cacheKey)!;
  }
  
  // Calcular contexto semântico
  const context = selectRelevantMessages(currentMessage, conversationHistory, maxMessages);
  
  // Adicionar ao cache (com limite de tamanho)
  if (semanticCache.size >= CACHE_SIZE_LIMIT) {
    const firstKey = semanticCache.keys().next().value;
    semanticCache.delete(firstKey);
  }
  semanticCache.set(cacheKey, context);
  
  return context;
}